import React, { useState, useEffect } from 'react';
import { X, User, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { Client } from '../types';
import apiService from '../services/api';

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onSuccess: () => void;
}

const EditClientModal: React.FC<EditClientModalProps> = ({
  isOpen,
  onClose,
  client,
  onSuccess
}) => {
  const [category, setCategory] = useState<'1-1' | 'group'>('1-1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && client) {
      setCategory(client.category || '1-1');
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, client]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!client) return;

    if (category === client.category) {
      // No changes
      onClose();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.updateClient(client.id, { category });
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
        setSuccess(false);
      }, 1500);
      
    } catch (err: any) {
      console.error('Error updating client:', err);
      setError(err.response?.data?.message || 'Failed to update client category');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Edit Client Category</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {success ? (
            <div className="text-center py-6">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Client Updated!
              </h3>
              <p className="text-gray-600">
                The client category has been updated successfully.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Client Info */}
              <div className="bg-gray-50 rounded-md p-3 mb-4">
                <p className="text-sm text-gray-600 mb-1">Client</p>
                <p className="text-sm font-medium text-gray-900">
                  {client.user.first_name} {client.user.last_name}
                </p>
                <p className="text-xs text-gray-500">{client.user.email}</p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: category === '1-1' ? '#3b82f6' : '#e5e7eb' }}>
                    <input
                      type="radio"
                      value="1-1"
                      checked={category === '1-1'}
                      onChange={(e) => setCategory(e.target.value as '1-1' | 'group')}
                      className="mr-3"
                      disabled={loading}
                    />
                    <User className="h-5 w-5 mr-2 text-purple-600" />
                    <div>
                      <div className="font-medium text-gray-900">1-on-1</div>
                      <div className="text-xs text-gray-500">Individual coaching</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-3 border-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{ borderColor: category === 'group' ? '#3b82f6' : '#e5e7eb' }}>
                    <input
                      type="radio"
                      value="group"
                      checked={category === 'group'}
                      onChange={(e) => setCategory(e.target.value as '1-1' | 'group')}
                      className="mr-3"
                      disabled={loading}
                    />
                    <Users className="h-5 w-5 mr-2 text-blue-600" />
                    <div>
                      <div className="font-medium text-gray-900">Group</div>
                      <div className="text-xs text-gray-500">Group coaching with training plans & zoom sessions</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Current Category Info */}
              {client.category !== category && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Changing to {category === 'group' ? 'Group' : '1-on-1'} will 
                    {category === 'group' 
                      ? ' enable access to group training plans and zoom sessions.'
                      : ' remove access to group-specific features.'}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || category === client.category}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Category'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditClientModal;

